import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import { userAPI } from '../../services/api';
import { 
  User, Building, Phone, Mail, MapPin, FileText,
  Save, Loader2, CheckCircle, AlertCircle, Camera
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const ProfilePage = () => {
  const { user, updateProfile } = useAuth();
  const { t, locale, availableLocales } = useI18n();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    address_street: '',
    address_city: '',
    address_zip: '',
    address_country: '',
    vat_id: '',
    locale: 'de',
    timezone: 'Europe/Berlin',
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await userAPI.getProfile();
        setFormData(prev => ({
          ...prev,
          ...response.data,
          email: response.data.email || '',
          name: response.data.name || '',
          company: response.data.company || '',
          phone: response.data.phone || '',
          address_street: response.data.address_street || '',
          address_city: response.data.address_city || '',
          address_zip: response.data.address_zip || '',
          address_country: response.data.address_country || '',
          vat_id: response.data.vat_id || '',
          locale: response.data.locale || 'de',
          timezone: response.data.timezone || 'Europe/Berlin',
        }));
      } catch (err) {
        console.error('Failed to fetch profile:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess(false);

    try {
      await userAPI.updateProfile(formData);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const timezones = [
    'Europe/Berlin', 'Europe/London', 'Europe/Paris', 'Europe/Rome',
    'Europe/Madrid', 'Europe/Amsterdam', 'Europe/Warsaw', 'Europe/Istanbul',
    'Europe/Moscow', 'Asia/Dubai', 'America/New_York', 'America/Los_Angeles'
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-8 h-8 border-2 border-[#EC2229] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="profile-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-2xl font-bold text-white">{t('nav.profile')}</h1>
          <p className="text-slate-400 mt-1">Verwalten Sie Ihre persönlichen Daten und Geschäftsinformationen</p>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-500" />
            <p className="text-emerald-400">Profil erfolgreich aktualisiert!</p>
          </div>
        )}
        
        {error && (
          <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <p className="text-red-400">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Profile Header Card */}
          <div className="glass-card p-6">
            <div className="flex items-center gap-6">
              <div className="relative">
                <div className="w-24 h-24 rounded-full bg-[#EC2229]/20 flex items-center justify-center">
                  <span className="text-4xl font-heading font-bold text-[#EC2229]">
                    {formData.name?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
                <button 
                  type="button"
                  className="absolute bottom-0 right-0 w-8 h-8 bg-[#EC2229] rounded-full flex items-center justify-center hover:bg-[#D01F25] transition-colors"
                >
                  <Camera className="w-4 h-4 text-white" />
                </button>
              </div>
              <div>
                <h2 className="font-heading text-xl font-semibold text-white">{formData.name}</h2>
                <p className="text-slate-400">{formData.email}</p>
                <span className="badge-neutral mt-2 inline-block">{user?.role}</span>
              </div>
            </div>
          </div>

          {/* Personal Information */}
          <div className="glass-card p-6">
            <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-[#EC2229]" />
              Persönliche Daten
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-text">Name *</Label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="input-field"
                  required
                  data-testid="profile-name"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="label-text">E-Mail</Label>
                <Input
                  name="email"
                  value={formData.email}
                  className="input-field"
                  disabled
                  data-testid="profile-email"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="label-text">Telefon</Label>
                <Input
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+49 172..."
                  className="input-field"
                  data-testid="profile-phone"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="label-text">Sprache</Label>
                <Select 
                  value={formData.locale} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, locale: value }))}
                >
                  <SelectTrigger className="input-field" data-testid="profile-locale">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#23272F] border-white/10">
                    {availableLocales.map(l => (
                      <SelectItem key={l.code} value={l.code}>
                        {l.flag} {l.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2 md:col-span-2">
                <Label className="label-text">Zeitzone</Label>
                <Select 
                  value={formData.timezone} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, timezone: value }))}
                >
                  <SelectTrigger className="input-field" data-testid="profile-timezone">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#23272F] border-white/10">
                    {timezones.map(tz => (
                      <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Business Information */}
          <div className="glass-card p-6">
            <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Building className="w-5 h-5 text-[#EC2229]" />
              Geschäftsdaten
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-text">Firma</Label>
                <Input
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Firmenname GmbH"
                  className="input-field"
                  data-testid="profile-company"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="label-text">USt-IdNr.</Label>
                <Input
                  name="vat_id"
                  value={formData.vat_id}
                  onChange={handleChange}
                  placeholder="DE123456789"
                  className="input-field"
                  data-testid="profile-vat"
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="glass-card p-6">
            <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#EC2229]" />
              Adresse
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label className="label-text">Straße</Label>
                <Input
                  name="address_street"
                  value={formData.address_street}
                  onChange={handleChange}
                  placeholder="Musterstraße 123"
                  className="input-field"
                  data-testid="profile-street"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="label-text">PLZ</Label>
                <Input
                  name="address_zip"
                  value={formData.address_zip}
                  onChange={handleChange}
                  placeholder="12345"
                  className="input-field"
                  data-testid="profile-zip"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="label-text">Stadt</Label>
                <Input
                  name="address_city"
                  value={formData.address_city}
                  onChange={handleChange}
                  placeholder="Berlin"
                  className="input-field"
                  data-testid="profile-city"
                />
              </div>
              
              <div className="space-y-2 md:col-span-2">
                <Label className="label-text">Land</Label>
                <Select 
                  value={formData.address_country || 'DE'} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, address_country: value }))}
                >
                  <SelectTrigger className="input-field" data-testid="profile-country">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#23272F] border-white/10">
                    <SelectItem value="DE">Deutschland</SelectItem>
                    <SelectItem value="AT">Österreich</SelectItem>
                    <SelectItem value="CH">Schweiz</SelectItem>
                    <SelectItem value="NL">Niederlande</SelectItem>
                    <SelectItem value="BE">Belgien</SelectItem>
                    <SelectItem value="FR">Frankreich</SelectItem>
                    <SelectItem value="IT">Italien</SelectItem>
                    <SelectItem value="ES">Spanien</SelectItem>
                    <SelectItem value="PL">Polen</SelectItem>
                    <SelectItem value="GB">Großbritannien</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <Button 
              type="submit" 
              disabled={saving}
              className="btn-primary"
              data-testid="profile-save"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Speichern...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Änderungen speichern
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default ProfilePage;
